import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ColumnDefinition } from 'src/app/components/qs-table/column-definition.interface';
import { selectProfileState } from 'src/app/stores/profile/profile.selector';
import { MasterAdminCompanyStore } from 'src/app/stores/master-admin-company.store';

type CompanyAction = 'delete' | null;
type CompanyRow = {
  id: string;
  name: string;
  country?: string;
  industry?: string;
};

@Component({
  selector: 'app-master-admin-companies',
  templateUrl: './master-admin-companies.component.html',
  styleUrls: ['./master-admin-companies.component.scss'],
  standalone: false,
})
export class MasterAdminCompaniesComponent implements OnInit {
  private readonly router = inject(Router);
  protected readonly companyStore = inject(MasterAdminCompanyStore);
  private readonly profile = toSignal(inject(Store).select(selectProfileState));
  @ViewChild('companyNameTemplate', { static: true })
  private companyNameTemplate!: TemplateRef<{ $implicit: CompanyRow }>;
  showResponseBanner = false;
  successfulSave = false;
  successMessage = '';
  errorMessage = '';
  readonly tablePaginatorConfig = {
    pageSize: 15,
    pageSizeOptions: [10, 25, 50],
  };
  /**
   * Recomputed rather than built once in ngOnInit: the privileged role arrives with the profile
   * from the backend, which can land after the page has already rendered.
   */
  readonly companyColumns = computed<ColumnDefinition<CompanyRow>[]>(() =>
    this.getCompanyColumns(),
  );
  readonly companyTableData = computed<CompanyRow[]>(() =>
    this.companyStore
      .companies()
      .slice()
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
  );
  private pendingAction: CompanyAction = null;

  /** A support user reads the companies; only a masteradmin may create, edit or delete one. */
  get canManageCompanies(): boolean {
    return this.profile()?.isMasteradmin ?? false;
  }

  constructor() {
    effect(() => {
      const saving = this.companyStore.saving();
      const error = this.companyStore.error();

      if (!this.pendingAction || saving) {
        return;
      }

      if (error) {
        this.successfulSave = false;
        this.errorMessage = this.resolveErrorMessage(
          error,
          'Failed to save company.',
        );
        this.showResponseBanner = true;
        this.pendingAction = null;
        return;
      }

      this.successfulSave = true;
      this.errorMessage = '';
      this.successMessage = this.getSuccessMessage(this.pendingAction);
      this.showResponseBanner = true;
      this.pendingAction = null;
      this.companyStore.loadCompanies();
    });
  }

  ngOnInit(): void {
    this.companyStore.clearError();
    this.companyStore.loadCompanies();
  }

  navigateToCreate(): void {
    this.router.navigate(['/admin/companies/create']);
  }

  navigateToEdit(companyId: string): void {
    this.router.navigate(['/admin/companies', companyId, 'edit']);
  }

  navigateToDetails(companyId: string): void {
    this.router.navigate(['/admin/companies', companyId]);
  }

  onDelete(companyId: string, companyName: string): void {
    if (
      !window.confirm(
        `Delete company "${companyName}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    this.pendingAction = 'delete';
    this.showResponseBanner = false;
    this.companyStore.deleteCompany(companyId);
  }

  private getCompanyColumns(): ColumnDefinition<CompanyRow>[] {
    return [
      {
        type: 'data',
        field: 'name',
        header: 'Name',
        customTemplate: this.companyNameTemplate,
      },
      {
        type: 'data',
        field: 'id',
        header: 'Company ID',
      },
      {
        type: 'data',
        field: 'country',
        header: 'Country',
        format: (row) => row?.country || '-',
      },
      {
        type: 'data',
        field: 'industry',
        header: 'Industry',
        format: (row) => row?.industry || '-',
      },
      {
        type: 'action',
        header: 'Actions',
        width: '72px',
        actionType: 'menu',
        actions: [
          {
            label: 'View Details',
            callback: (row: CompanyRow) => this.navigateToDetails(row.id),
          },
          ...(this.canManageCompanies
            ? [
                {
                  label: 'Edit',
                  callback: (row: CompanyRow) => this.navigateToEdit(row.id),
                },
                {
                  label: 'Delete',
                  callback: (row: CompanyRow) =>
                    this.onDelete(row.id, row.name || row.id),
                },
              ]
            : []),
        ],
      },
    ];
  }

  private getSuccessMessage(action: Exclude<CompanyAction, null>): string {
    switch (action) {
      case 'delete':
        return 'Company deleted successfully.';
      default:
        return 'Operation successful.';
    }
  }

  private resolveErrorMessage(error: unknown, fallback: string): string {
    if (!error) {
      return fallback;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object' && error !== null) {
      const typedError = error as {
        message?: string;
        error?: { message?: string; title?: string };
      };
      if (typedError.error?.message) {
        return typedError.error.message;
      }
      if (typedError.error?.title) {
        return typedError.error.title;
      }
      if (typedError.message) {
        return typedError.message;
      }
    }
    return fallback;
  }
}
